import moment from "moment";
import dateFormats from "../../app/data/common/dateFormats";
import { PayRate } from "../../app/data/drivers/models";
import { LinehaulTrip, LinehaulTripException } from "../../app/data/linehaul-mileage/models";
import { TripSettlementData, TripSettlementLineItem, TripSettlementRate } from "./settlementTypes";
import { parseRateAmount } from "./extractDriverPayInfo";

const HOUR_BASED_EXCEPTION_TYPES = new Set(["DELAY", "BREAKDOWN"]);

export interface DriverPayInfo {
  payRates?: PayRate[];
  hourlyRate?: number | string | null;
  perMileRate?: number | string | null;
}

const parseAmount = parseRateAmount;

const roundCurrency = (amount: number) => Math.round(amount * 100) / 100;

const formatRateCurrency = (amount: number) =>
  amount.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getEffectivePayRates = (payInfo?: DriverPayInfo) => {
  let perMile = parseAmount(payInfo?.perMileRate);
  let hourly = parseAmount(payInfo?.hourlyRate);

  payInfo?.payRates?.forEach((rate) => {
    const effective = parseAmount(rate.amount);
    if (effective == null) return;

    const adjusted = effective + (parseAmount(rate.adjustment) ?? 0);
    const payType = rate.payType?.toUpperCase() ?? "";

    if (payType.includes("MILE") || payType.includes("MILEAGE") || payType.includes("PER_MILE")) {
      perMile = adjusted;
    }
    if (payType.includes("HOUR") || payType.includes("HOURLY")) {
      hourly = adjusted;
    }
  });

  return { perMile, hourly };
};

export const hasConfiguredPayRates = (payInfo?: DriverPayInfo): boolean => {
  const { perMile, hourly } = getEffectivePayRates(payInfo);
  return perMile != null || hourly != null;
};

const buildRateLabels = (perMile?: number, hourly?: number): TripSettlementRate[] => {
  const rates: TripSettlementRate[] = [];
  if (perMile != null) {
    rates.push({ label: `${formatRateCurrency(perMile)}/mi` });
  }
  if (hourly != null) {
    rates.push({ label: `${formatRateCurrency(hourly)}/hr` });
  }
  return rates;
};

const formatTerminalLabel = (airportCode: string, terminalName: string, terminalNumber: number) =>
  `${airportCode} – ${terminalName} ${terminalNumber}`;

const buildLaneSubtitle = (trip: LinehaulTrip, perMile?: number) => {
  const origin = formatTerminalLabel(
    trip.originTerminalAirportcode,
    trip.originTerminalName,
    trip.originTerminalNumber
  );
  const destination = formatTerminalLabel(
    trip.destinationTerminalAirportcode,
    trip.destinationTerminalName,
    trip.destinationTerminalNumber
  );
  const dispatchedDate = moment(trip.dispatchedDate, "YYYYMMDD").format(dateFormats.uiDate);
  const miles = trip.lane?.miles ?? 0;

  const rateDetail = perMile != null ? ` • ${miles} mi × ${formatRateCurrency(perMile)}/mi` : ` • ${miles} mi`;

  return `${origin} → ${destination} • Assigned: ${dispatchedDate}${rateDetail}`;
};

const formatExceptionTitle = (type: string) =>
  type.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const computeExceptionAmount = (exception: LinehaulTripException, hourlyRate?: number): number => {
  if (HOUR_BASED_EXCEPTION_TYPES.has(exception.type)) {
    const hours = parseAmount(exception.duration);
    if (hours == null || hourlyRate == null) return 0;
    return roundCurrency(hours * hourlyRate);
  }

  return roundCurrency(parseAmount(exception.amount) ?? 0);
};

const buildExceptionSubtitle = (exception: LinehaulTripException, hourlyRate?: number) => {
  if (!HOUR_BASED_EXCEPTION_TYPES.has(exception.type)) return undefined;

  const hours = parseAmount(exception.duration);
  if (hours == null) return undefined;

  const rateDetail = hourlyRate != null ? ` • ${hours} hrs × ${formatRateCurrency(hourlyRate)}/hr` : ` • ${hours} hrs`;
  return `${exception.reason}${rateDetail}`;
};

export const buildTripSettlementData = (
  trip?: LinehaulTrip,
  payInfo?: DriverPayInfo
): TripSettlementData | null => {
  if (!trip) return null;

  const { perMile, hourly } = getEffectivePayRates(payInfo);
  const lineItems: TripSettlementLineItem[] = [];
  let mileagePay = 0;
  let exceptionPay = 0;

  if (trip.lane) {
    const laneAmount = perMile != null ? roundCurrency(trip.lane.miles * perMile) : 0;
    mileagePay = laneAmount;
    lineItems.push({
      id: `lane-${trip.lane.laneId}`,
      type: "lane",
      title: trip.lane.laneName,
      subtitle: buildLaneSubtitle(trip, perMile),
      amount: laneAmount,
    });
  }

  trip.exceptions?.forEach((exception) => {
    const amount = computeExceptionAmount(exception, hourly);
    exceptionPay = roundCurrency(exceptionPay + amount);

    lineItems.push({
      id: `exception-${exception.exceptionId ?? exception.type}`,
      type: "exception",
      title: formatExceptionTitle(exception.type),
      subtitle: buildExceptionSubtitle(exception, hourly),
      amount,
    });
  });

  const driver = trip.driver;

  return {
    driverName: driver ? `${driver.firstName} ${driver.lastName}` : "No driver assigned",
    driverId: driver?.driverId ?? "—",
    rates: buildRateLabels(perMile, hourly),
    lineItems,
    summary: {
      mileagePay,
      exceptionPay,
      totalSettlement: roundCurrency(mileagePay + exceptionPay),
    },
    missingPayRates: !!driver && !!trip.lane && perMile == null,
  };
};
